import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChartBarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { analyticsAPI } from '../services/api';
import InsightsTabs from '../components/common/InsightsTabs';

const cardClass = 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm';

const OUTCOME_LABELS = {
  bounced: 'Left without asking',
  dropped_qualification: 'Left before sharing details',
  escalated: 'Asked for a person',
  abandoned: 'Stopped replying',
  other: 'Other',
};

export default function MissedOpportunitiesPage() {
  const { tenantId } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notEntitled, setNotEntitled] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setNotEntitled(false);
    const params = tenantId ? { days: 30, tenant_id: tenantId } : { days: 30 };
    analyticsAPI.getMissedOpportunitiesSummary(params)
      .then((response) => {
        if (active) setSummary(response.data);
      })
      .catch((err) => {
        if (!active) return;
        if (err.response?.status === 403) {
          setNotEntitled(true);
        } else {
          setError(err.response?.data?.detail || 'Could not load Missed Opportunity Intelligence.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [tenantId]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-100 border-t-indigo-600" />
      </div>
    );
  }

  if (notEntitled) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <div className={`${cardClass} p-6 text-center`}>
          <ChartBarIcon className="mx-auto h-10 w-10 text-indigo-400" />
          <h1 className="mt-3 text-xl font-bold text-gray-900 dark:text-white">Missed Opportunity Intelligence</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            This is available on the Scale plan. Talk to Serviti to see it on your account.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 max-w-3xl mx-auto">
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  const {
    meaningful_conversations: meaningfulConversations,
    leads_captured: leadsCaptured,
    missed_count: missedCount,
    conversion_rate_pct: conversionRatePct,
    drop_off_breakdown: dropOffBreakdown,
    drop_off_samples: dropOffSamples,
    agents,
  } = summary;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <InsightsTabs />
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <ChartBarIcon className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
          Missed Opportunities
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          What happened in the conversations that didn't become leads, last 30 days.
        </p>
      </div>

      {/* Headline banner -- the whole pitch in one glance */}
      <div className="rounded-lg bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-5 sm:p-6 shadow-md">
        {meaningfulConversations === 0 ? (
          <p className="text-base sm:text-lg font-semibold">
            No meaningful conversations analyzed yet in the last 30 days. Check back once your agent has been chatting with visitors for a few days.
          </p>
        ) : (
          <p className="text-base sm:text-lg font-semibold leading-relaxed">
            You had <span className="font-extrabold">{meaningfulConversations}</span> meaningful enquiries this month.
            Only <span className="font-extrabold">{leadsCaptured}</span> became leads ({conversionRatePct}%).
            Here's what happened to the other <span className="font-extrabold">{missedCount}</span>.
          </p>
        )}
      </div>

      {meaningfulConversations > 0 && missedCount > 0 && (
        // Each drop-off was a question the agent could not turn into a lead.
        // Most visitors left no details, so the useful moves are to read what
        // happened and give the agent the answer it lacked.
        <div className="flex flex-wrap gap-2">
          <Link
            to={tenantId ? `/t/${tenantId}/rag/sources` : '/platform/rag/sources'}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            Add the missing answers to your knowledge
          </Link>
          <Link
            to={tenantId ? `/t/${tenantId}/conversations` : '/platform/conversations'}
            className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
          >
            Review conversations
          </Link>
        </div>
      )}

      {meaningfulConversations > 0 && (
        <>
          {/* Drop-off moments -- grouped by outcome, a fixed small taxonomy.
              Free-text theme clustering ("what did they actually ask") is a
              separate, larger piece; this is the cheap, always-available view. */}
          <div className={`${cardClass} p-5`}>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
              Where visitors dropped off
            </h2>
            {dropOffBreakdown.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No drop-offs in this window — nice.</p>
            ) : (
              <div className="mt-4 space-y-2">
                {dropOffBreakdown.map((row) => {
                  const pct = missedCount ? Math.round((row.count / missedCount) * 100) : 0;
                  return (
                    <div key={row.outcome} className="flex items-center gap-3">
                      <span className="w-48 shrink-0 text-sm text-gray-700 dark:text-gray-300">
                        {OUTCOME_LABELS[row.outcome] || row.outcome}
                      </span>
                      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-16 shrink-0 text-right text-sm font-semibold text-gray-900 dark:text-white">
                        {row.count}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {dropOffSamples.length > 0 && (
              <div className="mt-5 border-t border-gray-100 dark:border-gray-700 pt-4">
                <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Examples
                </h3>
                <ul className="mt-2 space-y-2">
                  {dropOffSamples.map((sample, idx) => (
                    <li key={idx} className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
                      <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                      <span className="flex-1">{sample.marker}</span>
                      {sample.conversation_id && sample.source_type !== 'bsp' && (
                        <Link
                          to={`${tenantId ? `/t/${tenantId}` : '/platform'}/conversations?open=${encodeURIComponent(sample.conversation_id)}`}
                          className="shrink-0 font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
                        >
                          Open conversation
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Agent conversion comparison */}
          <div className={`${cardClass} p-5`}>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
              Conversion by agent
            </h2>
            {agents.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No agent data in this window.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      <th className="pb-2 pr-4">Agent</th>
                      <th className="pb-2 pr-4">Conversations</th>
                      <th className="pb-2 pr-4">Leads captured</th>
                      <th className="pb-2 pr-4">Left without asking</th>
                      <th className="pb-2">Conversion rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {agents.map((agent) => (
                      <tr key={agent.agent}>
                        <td className="py-2 pr-4 font-medium text-gray-900 dark:text-white">{agent.agent}</td>
                        <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{agent.total}</td>
                        <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{agent.lead_captured}</td>
                        <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{agent.bounced}</td>
                        <td className="py-2 font-semibold text-gray-900 dark:text-white">{agent.capture_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
